import { useState, useEffect } from 'react'
import { apiBase } from './lib/api'

const API = apiBase

// ── Auth ──────────────────────────────────────────────────────────────────────
function useAdminAuth() {
  const [token,   setToken]   = useState(null)
  const [user,    setUser]    = useState(null)
  const [ready,   setReady]   = useState(false)
  const [email,   setEmail]   = useState('')
  const [pwd,     setPwd]     = useState('')
  const [error,   setError]   = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const t = localStorage.getItem('backend_token')
    if (!t) { setReady(true); return }
    fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.id && d?.role === 'admin') { setToken(t); setUser(d) } else localStorage.removeItem('backend_token') })
      .catch(() => localStorage.removeItem('backend_token'))
      .finally(() => setReady(true))
  }, [])

  const login = async (e) => {
    e.preventDefault(); setError(''); setLoading(true)
    try {
      const res  = await fetch(`${API}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim(), password: pwd }) })
      const data = await res.json()
      if (!res.ok)                       { setError(data.error || 'Credenciales incorrectas'); return }
      if (data.user?.role !== 'admin')   { setError('Sin permisos de administrador'); return }
      localStorage.setItem('backend_token', data.token)
      setToken(data.token); setUser(data.user)
    } catch { setError('Error de red.') } finally { setLoading(false) }
  }

  const logout = () => { localStorage.removeItem('backend_token'); setToken(null); setUser(null) }
  return { token, user, ready, email, setEmail, pwd, setPwd, error, loading, login, logout }
}

// ── Utils ─────────────────────────────────────────────────────────────────────
const fmtDate = ts => new Date(ts * 1000).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })
const fmtFull = ts => new Date(ts * 1000).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
const PLANS = ['free', 'explorador', 'atleta', 'familiar', 'pro']
const ROLES = ['user', 'admin']

// ── Overview ──────────────────────────────────────────────────────────────────
function Overview({ token }) {
  const [stats, setStats] = useState(null)

  useEffect(() => {
    fetch(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setStats)
  }, [token])

  if (!stats) return <Spinner />

  const planMap = {}
  stats.byPlan.forEach(p => { planMap[p.plan] = p.n })
  const conv = stats.totalLeads > 0 ? Math.round(stats.convertedLeads / stats.totalLeads * 100) : 0

  const kpis = [
    { label: 'Usuarios',       value: stats.totalUsers },
    { label: 'Leads',          value: stats.totalLeads },
    { label: 'Convertidos',    value: `${stats.convertedLeads} (${conv}%)` },
    { label: 'Demos activos',  value: stats.activeDemos },
    { label: 'Seguidores',     value: stats.totalFollowers },
  ]

  return (
    <div>
      <PageTitle>Resumen</PageTitle>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: '#e5e7eb', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: '#fff', padding: '22px 24px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', letterSpacing: -0.5 }}>{k.value}</div>
          </div>
        ))}
      </div>

      <SectionLabel>Usuarios por plan</SectionLabel>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <Th>Plan</Th>
              <Th align="right">Usuarios</Th>
              <Th align="right">% del total</Th>
            </tr>
          </thead>
          <tbody>
            {PLANS.map((p, i) => {
              const n = planMap[p] ?? 0
              const pct = stats.totalUsers > 0 ? Math.round(n / stats.totalUsers * 100) : 0
              return (
                <tr key={p} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : undefined }}>
                  <Td><span style={{ fontWeight: 500, color: '#111827' }}>{p}</span></Td>
                  <Td align="right">{n}</Td>
                  <Td align="right">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
                      <div style={{ width: 80, height: 4, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: '#4f46e5', borderRadius: 2 }} />
                      </div>
                      <span style={{ minWidth: 32, textAlign: 'right' }}>{pct}%</span>
                    </div>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Users ─────────────────────────────────────────────────────────────────────
function Users({ token }) {
  const [users,    setUsers]    = useState([])
  const [selected, setSelected] = useState(null)
  const [search,   setSearch]   = useState('')

  useEffect(() => {
    fetch(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setUsers)
  }, [token])

  const handleSaved = u => {
    setUsers(prev => prev.map(x => x.id === u.id ? { ...x, ...u } : x))
    setSelected(s => s ? { ...s, ...u } : s)
  }

  const filtered = users.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 340px' : '1fr', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <PageTitle style={{ margin: 0, flex: 1 }}>Usuarios <Muted>({users.length})</Muted></PageTitle>
          <input placeholder="Buscar por nombre o email…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inp, width: 220, fontSize: 13, padding: '7px 12px' }} />
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <Th>Nombre</Th>
                <Th>Email</Th>
                <Th>Plan</Th>
                <Th>Rol</Th>
                <Th align="right">Registro</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ padding: '20px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>Sin resultados.</td></tr>
              )}
              {filtered.map((u, i) => (
                <tr
                  key={u.id}
                  onClick={() => setSelected(selected?.id === u.id ? null : u)}
                  style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : undefined, cursor: 'pointer', background: selected?.id === u.id ? '#eef2ff' : undefined, transition: 'background 0.1s' }}
                >
                  <Td><span style={{ fontWeight: 500, color: '#111827' }}>{u.name}</span></Td>
                  <Td>{u.email}</Td>
                  <Td><Badge>{u.plan}</Badge></Td>
                  <Td>{u.role === 'admin' ? <Badge danger>admin</Badge> : <span style={{ color: '#9ca3af', fontSize: 12 }}>user</span>}</Td>
                  <Td align="right">{fmtDate(u.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <UserDrawer key={selected.id} user={selected} token={token} onClose={() => setSelected(null)} onSaved={handleSaved} />
      )}
    </div>
  )
}

function UserDrawer({ user, token, onClose, onSaved }) {
  const [detail,  setDetail]  = useState(null)
  const [form,    setForm]    = useState({ name: user.name, email: user.email, plan: user.plan, role: user.role })
  const [saving,  setSaving]  = useState(false)
  const [saved,   setSaved]   = useState(false)
  const [err,     setErr]     = useState('')

  useEffect(() => {
    fetch(`${API}/admin/users/${user.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setDetail)
  }, [user.id])

  const save = async () => {
    setSaving(true); setErr('')
    const res  = await fetch(`${API}/admin/users/${user.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) })
    const json = await res.json()
    if (!res.ok) { setErr(json.error || 'Error al guardar'); setSaving(false); return }
    setSaved(true); setTimeout(() => setSaved(false), 2000)
    onSaved(json); setSaving(false)
  }

  const bmi = detail?.weight_kg && detail?.height_cm
    ? (detail.weight_kg / ((detail.height_cm / 100) ** 2)).toFixed(1) : null

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', position: 'sticky', top: 20 }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{user.name}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '2px 4px' }}>×</button>
      </div>

      <div style={{ padding: '16px 20px' }}>
        {/* Meta */}
        {detail ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {[
              ['Registrado', fmtFull(detail.created_at)],
              ['ID', detail.id.slice(0, 14) + '…'],
              ...(detail.age       ? [['Edad',   `${detail.age} años`]]  : []),
              ...(detail.weight_kg ? [['Peso',   `${detail.weight_kg} kg`]] : []),
              ...(detail.height_cm ? [['Altura', `${detail.height_cm} cm`]] : []),
              ...(bmi              ? [['IMC',    bmi]]                    : []),
            ].map(([k, v]) => (
              <div key={k} style={{ background: '#f9fafb', borderRadius: 8, padding: '9px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 12, color: '#374151', wordBreak: 'break-all' }}>{v}</div>
              </div>
            ))}
          </div>
        ) : <Spinner small />}

        {/* Editable */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Field label="Nombre">
            <input style={inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Email">
            <input type="email" style={inp} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Plan">
              <select style={sel} value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
                {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Rol">
              <select style={sel} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
        </div>

        {err && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>{err}</p>}

        <button onClick={save} disabled={saving} style={{ marginTop: 14, width: '100%', background: saved ? '#16a34a' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1, transition: 'background 0.2s' }}>
          {saving ? 'Guardando…' : saved ? 'Guardado' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  )
}

// ── Leads ─────────────────────────────────────────────────────────────────────
function Leads({ token }) {
  const [leads, setLeads] = useState([])

  useEffect(() => {
    fetch(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setLeads)
  }, [token])

  return (
    <div>
      <PageTitle>Leads <Muted>({leads.length})</Muted></PageTitle>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <Th>Nombre</Th>
              <Th>Email</Th>
              <Th>Teléfono</Th>
              <Th>Plan</Th>
              <Th>Estado</Th>
              <Th align="right">Fecha</Th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={6} style={{ padding: '20px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>Sin leads todavía.</td></tr>
            )}
            {leads.map((l, i) => (
              <tr key={l.id} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : undefined }}>
                <Td><span style={{ fontWeight: 500, color: '#111827' }}>{l.name}</span></Td>
                <Td>{l.email}</Td>
                <Td>{l.phone || <span style={{ color: '#d1d5db' }}>—</span>}</Td>
                <Td><Badge>{l.plan}</Badge></Td>
                <Td>{l.activated ? <Badge success>Activado</Badge> : <Badge>Pendiente</Badge>}</Td>
                <Td align="right">{fmtFull(l.created_at)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Demos ─────────────────────────────────────────────────────────────────────
function Demos({ token }) {
  const [links,   setLinks]   = useState([])
  const [label,   setLabel]   = useState('')
  const [loading, setLoading] = useState(false)
  const [copied,  setCopied]  = useState(null)

  const load = () =>
    fetch(`${API}/admin/demo-links`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setLinks)

  useEffect(() => { load() }, [token])

  const create = async () => {
    setLoading(true)
    const res = await fetch(`${API}/admin/demo-links`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ label: label.trim() || 'Demo' }) })
    if (res.ok) { setLabel(''); await load() }
    setLoading(false)
  }

  const remove = async id => {
    await fetch(`${API}/admin/demo-links/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
    load()
  }

  const copy = (link, id) => { navigator.clipboard.writeText(link); setCopied(id); setTimeout(() => setCopied(null), 2000) }

  const now      = Math.floor(Date.now() / 1000)
  const monthAgo = now - 30 * 86400
  const active   = links.filter(l => l.expires_at >= now)
  const expMonth = links.filter(l => l.expires_at < now && l.expires_at >= monthAgo)
  const expOlder = links.filter(l => l.expires_at < monthAgo)

  const kpis = [
    { label: 'Activos ahora',     value: active.length },
    { label: 'Vencidos este mes', value: expMonth.length },
    { label: 'Total histórico',   value: links.length },
  ]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <PageTitle style={{ margin: 0, flex: 1 }}>Links de demo</PageTitle>
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Nombre (ej: Juan Pérez)"
          onKeyDown={e => e.key === 'Enter' && create()}
          style={{ ...inp, width: 220, fontSize: 13, padding: '7px 12px' }} />
        <button onClick={create} disabled={loading}
          style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: loading ? 0.6 : 1 }}>
          {loading ? '…' : 'Crear link'}
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: '#e5e7eb', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: '#fff', padding: '18px 22px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', letterSpacing: -0.5 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabla activos */}
      <SectionLabel>Activos ({active.length})</SectionLabel>
      <DemoTable links={active} copied={copied} onCopy={copy} onRemove={remove} empty="Ningún link activo en este momento." />

      {/* Tabla vencidos este mes */}
      {expMonth.length > 0 && (
        <>
          <SectionLabel style={{ marginTop: 24 }}>Vencidos este mes ({expMonth.length})</SectionLabel>
          <DemoTable links={expMonth} copied={copied} onCopy={copy} onRemove={remove} muted />
        </>
      )}

      {/* Anteriores colapsable */}
      {expOlder.length > 0 && (
        <OlderSection count={expOlder.length}>
          <DemoTable links={expOlder} copied={copied} onCopy={copy} onRemove={remove} muted />
        </OlderSection>
      )}
    </div>
  )
}

function DemoTable({ links, copied, onCopy, onRemove, empty, muted }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 4 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <Th>Nombre</Th>
            <Th>Link</Th>
            <Th align="center">Usado</Th>
            <Th align="right">Vencimiento</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {links.length === 0 && (
            <tr><td colSpan={5} style={{ padding: '18px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>{empty}</td></tr>
          )}
          {links.map((l, i) => (
            <tr key={l.id} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : undefined }}>
              <Td><span style={{ fontWeight: 500, color: muted ? '#6b7280' : '#111827' }}>{l.label}</span></Td>
              <Td>
                <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220, display: 'block' }}>
                  {l.link}
                </span>
              </Td>
              <Td align="center"><span style={{ fontSize: 12, color: '#6b7280' }}>{l.activated_count}×</span></Td>
              <Td align="right"><span style={{ fontSize: 12, color: muted ? '#9ca3af' : '#374151' }}>{fmtFull(l.expires_at)}</span></Td>
              <Td align="right">
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button onClick={() => onCopy(l.link, l.id)}
                    style={{ background: copied === l.id ? '#16a34a' : '#f3f4f6', color: copied === l.id ? '#fff' : '#374151', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}>
                    {copied === l.id ? 'Copiado' : 'Copiar'}
                  </button>
                  <button onClick={() => onRemove(l.id)}
                    style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 13, cursor: 'pointer', color: '#dc2626' }}>×</button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function OlderSection({ count, children }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ marginTop: 24 }}>
      <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <SectionLabel style={{ margin: 0 }}>Anteriores ({count})</SectionLabel>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && children}
    </div>
  )
}

// ── Shared components ─────────────────────────────────────────────────────────
function PageTitle({ children, style }) {
  return <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', letterSpacing: -0.4, marginBottom: 18, ...style }}>{children}</h2>
}

function SectionLabel({ children, style }) {
  return <p style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, ...style }}>{children}</p>
}

function Muted({ children }) {
  return <span style={{ fontWeight: 400, color: '#9ca3af' }}>{children}</span>
}

function Badge({ children, danger, success }) {
  const bg = danger ? '#fef2f2' : success ? '#f0fdf4' : '#f3f4f6'
  const fg = danger ? '#dc2626' : success ? '#16a34a' : '#374151'
  return <span style={{ display: 'inline-block', background: bg, color: fg, border: `1px solid ${danger ? '#fecaca' : success ? '#bbf7d0' : '#e5e7eb'}`, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{children}</span>
}

function Th({ children, align }) {
  return <th style={{ padding: '10px 16px', textAlign: align || 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' }}>{children}</th>
}

function Td({ children, align }) {
  return <td style={{ padding: '11px 16px', fontSize: 13, color: '#4b5563', textAlign: align || 'left', verticalAlign: 'middle' }}>{children}</td>
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  )
}

function Spinner({ small }) {
  const size = small ? 18 : 28
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: size, height: size, borderRadius: '50%', border: `2px solid #e5e7eb`, borderTopColor: '#4f46e5', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

const inp = { background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827' }
const sel = { ...inp, cursor: 'pointer' }

// ── Login ─────────────────────────────────────────────────────────────────────
function Login({ email, setEmail, pwd, setPwd, error, loading, login }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{ width: 40, height: 40, background: '#4f46e5', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 8v8M8 12h8"/></svg>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4, letterSpacing: -0.5 }}>Manabi Backend</h1>
          <p style={{ fontSize: 14, color: '#6b7280' }}>Ingresá con tu cuenta de administrador</p>
        </div>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '28px 28px' }}>
          <form onSubmit={login} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Email">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus required placeholder="admin@ejemplo.com" style={inp} />
            </Field>
            <Field label="Contraseña">
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} required placeholder="••••••••" style={inp} />
            </Field>
            {error && <p style={{ fontSize: 13, color: '#dc2626', textAlign: 'center', margin: 0 }}>{error}</p>}
            <button type="submit" disabled={loading}
              style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1, marginTop: 4 }}>
              {loading ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

// ── Infraestructura ───────────────────────────────────────────────────────────
function Infra({ token }) {
  const [data,   setData]   = useState(null)
  const [err,    setErr]    = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetch(`${API}/admin/bq-stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(setData)
      .catch(e => setErr(`Error al cargar datos (${e})`))
  }, [token])

  if (err)   return <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p>
  if (!data) return <Spinner />

  const onlineCount = data.perUser?.filter(u => u.online).length ?? 0

  const kpis = [
    { label: 'Sesiones totales',  value: data.totalSessions?.toLocaleString('es-AR') ?? '—' },
    { label: 'Lecturas totales',  value: data.totalReadings?.toLocaleString('es-AR')  ?? '—' },
    { label: 'Almacenamiento',    value: data.totalBytesLabel ?? '—' },
    { label: 'En línea ahora',    value: onlineCount, accent: onlineCount > 0 },
    { label: 'Última sesión',     value: data.lastSession ? new Date(data.lastSession).toLocaleDateString('es-AR') : '—' },
  ]

  const filtered = (data.perUser ?? []).filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <PageTitle>Infraestructura</PageTitle>

      {/* Global KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1, background: '#e5e7eb', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ background: '#fff', padding: '20px 22px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: k.accent ? '#16a34a' : '#111827', letterSpacing: -0.5 }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* BigQuery job cost */}
      {data.jobs && (
        <>
          <SectionLabel>Consultas BigQuery — últimos 30 días</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1, background: '#e5e7eb', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
            {[
              { label: 'Jobs ejecutados',    value: Number(data.jobs.jobCount).toLocaleString('es-AR') },
              { label: 'Datos procesados',   value: data.jobs.bytesProcessedLabel },
              { label: 'Slot-hours',         value: data.jobs.slotHours },
              { label: 'Costo estimado',     value: `$${data.jobs.costUsd} USD` },
            ].map(k => (
              <div key={k.label} style={{ background: '#fff', padding: '20px 22px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: -0.5 }}>{k.value}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Table sizes */}
      {data.tables?.length > 0 && (
        <>
          <SectionLabel>Tablas en BigQuery</SectionLabel>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 28 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <Th>Tabla</Th>
                  <Th align="right">Filas</Th>
                  <Th align="right">Almacenamiento</Th>
                  <Th align="right">Última modificación</Th>
                </tr>
              </thead>
              <tbody>
                {data.tables.map((t, i) => (
                  <tr key={t.table} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : undefined }}>
                    <Td><span style={{ fontWeight: 500, color: '#111827', fontFamily: 'monospace', fontSize: 12 }}>{t.table}</span></Td>
                    <Td align="right">{Number(t.rows).toLocaleString('es-AR')}</Td>
                    <Td align="right">{t.bytesLabel}</Td>
                    <Td align="right"><span style={{ fontSize: 12, color: '#6b7280' }}>{t.lastModified ? new Date(t.lastModified).toLocaleDateString('es-AR') : '—'}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Per-user breakdown */}
      {data.perUser?.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <SectionLabel style={{ margin: 0, flex: 1 }}>Consumo por usuario</SectionLabel>
            <input
              placeholder="Filtrar por nombre o email…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 240, color: '#111827' }}
            />
          </div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <Th>Estado</Th>
                  <Th>Usuario</Th>
                  <Th>Email</Th>
                  <Th align="right">Sesiones</Th>
                  <Th align="right">Lecturas</Th>
                  <Th align="right">Storage</Th>
                  <Th align="right">Última sesión</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: '18px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>Sin resultados.</td></tr>
                )}
                {filtered.map((u, i) => (
                  <tr key={u.userId ?? i} style={{ borderTop: i > 0 ? '1px solid #f3f4f6' : undefined }}>
                    <Td>
                      {u.online
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 5, padding: '2px 8px' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                            En línea
                          </span>
                        : <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d1d5db', display: 'inline-block' }} />
                      }
                    </Td>
                    <Td><span style={{ fontWeight: 500, color: '#111827' }}>{u.name}</span></Td>
                    <Td><span style={{ fontSize: 12, color: '#6b7280' }}>{u.email || <span style={{ color: '#d1d5db' }}>—</span>}</span></Td>
                    <Td align="right">{Number(u.sessions).toLocaleString('es-AR')}</Td>
                    <Td align="right">{Number(u.readings).toLocaleString('es-AR')}</Td>
                    <Td align="right"><span style={{ fontSize: 12, color: '#6b7280' }}>{u.storageLabel ?? '—'}</span></Td>
                    <Td align="right"><span style={{ fontSize: 12, color: '#6b7280' }}>{u.lastSession ? new Date(u.lastSession).toLocaleDateString('es-AR') : '—'}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ── Sidebar nav ───────────────────────────────────────────────────────────────
const NAV = [
  { key: 'overview', label: 'Resumen' },
  { key: 'users',    label: 'Usuarios' },
  { key: 'leads',    label: 'Leads' },
  { key: 'demos',    label: 'Demos' },
  { key: 'infra',    label: 'Infraestructura' },
]

// ── Hash-based routing ────────────────────────────────────────────────────────
const VALID_PAGES = new Set(NAV.map(n => n.key))

function usePage() {
  const [page, setPageState] = useState(() => {
    const h = window.location.hash.replace('#', '')
    return VALID_PAGES.has(h) ? h : 'overview'
  })

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace('#', '')
      setPageState(VALID_PAGES.has(h) ? h : 'overview')
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const setPage = (key) => { window.location.hash = key }
  return [page, setPage]
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function Backend() {
  const auth = useAdminAuth()
  const [page, setPage] = usePage()

  if (!auth.ready) return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner />
    </div>
  )

  if (!auth.token) return <Login {...auth} />

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif', display: 'flex' }}>

      {/* Sidebar */}
      <aside style={{ width: 220, background: '#fff', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'sticky', top: 0, height: '100vh' }}>
        {/* Logo */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, background: '#4f46e5', borderRadius: 7, flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>Manabi</p>
              <p style={{ fontSize: 11, color: '#9ca3af' }}>Backend</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 10px' }}>
          {NAV.map(n => (
            <button key={n.key} onClick={() => setPage(n.key)} style={{
              display: 'block', width: '100%', textAlign: 'left',
              background: page === n.key ? '#eef2ff' : 'none',
              color: page === n.key ? '#4f46e5' : '#374151',
              border: 'none', borderRadius: 7, padding: '8px 12px',
              fontSize: 14, fontWeight: page === n.key ? 600 : 400,
              cursor: 'pointer', fontFamily: 'inherit', marginBottom: 2,
              transition: 'all 0.1s',
            }}>{n.label}</button>
          ))}
        </nav>

        {/* User */}
        <div style={{ padding: '14px 16px', borderTop: '1px solid #f3f4f6' }}>
          <p style={{ fontSize: 12, color: '#374151', fontWeight: 500, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{auth.user?.email}</p>
          <button onClick={auth.logout} style={{ background: 'none', border: 'none', fontSize: 12, color: '#9ca3af', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Content */}
      <main style={{ flex: 1, padding: 'clamp(24px, 3vw, 36px)', minWidth: 0, overflowX: 'auto' }}>
        {page === 'overview' && <Overview token={auth.token} />}
        {page === 'users'    && <Users    token={auth.token} />}
        {page === 'leads'    && <Leads    token={auth.token} />}
        {page === 'demos'    && <Demos    token={auth.token} />}
        {page === 'infra'    && <Infra    token={auth.token} />}
      </main>
    </div>
  )
}
